import { useRef, useEffect } from 'react';
import { ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldLabel } from '@/components/ui/field';
import { SignatureField } from '@/components/ui/app/signature-field';
import { BackButton } from '@/components/ui/app/back-button';
import { colorWithAlpha } from '@/lib/utils/utils';
import { backendToFrontendFieldType, isVideoFieldType } from '@/lib/utils/procedure-field-types';
import { PhotoFieldCreate, PhotoFieldEdit } from './PhotoField';
import { VideoFieldCreate, VideoFieldEdit } from './VideoField';
import PhotoCaptureSheet from './PhotoCaptureSheet';
import VideoCaptureSheet from './VideoCaptureSheet';
import { useTranslation } from 'react-i18next';

// ─── Sous-composants ────────────────────────────────────────────────────────────

/**
 * Rendu d'un champ de formulaire (tous types sauf photo/vidéo)
 */
function FieldRenderer({ field, value, onChange }) {
    const { t } = useTranslation();
    const frontendType = backendToFrontendFieldType(field.type);

    switch (frontendType) {
        case 'dropdown':
            return (
                <div>
                    <label
                        data-required={field.required || undefined}
                        className="text-[13px] text-foreground mb-2 block"
                    >
                        {field.label}
                    </label>
                    <select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-secondary rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors appearance-none cursor-pointer"
                        style={{
                            backgroundImage: `url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23030213' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 16px center',
                        }}
                    >
                        <option value="">{t('procedures:selectionnez')}</option>
                        {(field.options || []).map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </select>
                </div>
            );

        case 'text':
            return (
                <div>
                    <label
                        data-required={field.required || undefined}
                        className="text-[13px] text-foreground mb-2 block"
                    >
                        {field.label}
                    </label>
                    <input
                        type="text"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || ''}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-secondary rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors"
                    />
                </div>
            );

        case 'date':
            return (
                <div>
                    <label
                        data-required={field.required || undefined}
                        className="text-[13px] text-foreground mb-2 block"
                    >
                        {field.label}
                    </label>
                    <input
                        type="date"
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-secondary rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors"
                    />
                </div>
            );

        case 'comment':
            return (
                <div>
                    <label
                        data-required={field.required || undefined}
                        className="text-[13px] text-foreground mb-2 block"
                    >
                        {field.label}
                    </label>
                    <textarea
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        placeholder={field.placeholder || ''}
                        rows={4}
                        className="w-full bg-surface border border-border focus:border-2 focus:border-secondary rounded-[12px] px-[16px] py-[12px] text-[14px] text-foreground outline-none transition-colors resize-none"
                    />
                </div>
            );

        case 'checkbox': {
            const selectedOptions = Array.isArray(value) ? value : [];
            return (
                <Field>
                    <FieldLabel required={field.required}>{field.label}</FieldLabel>
                    <div className="space-y-[12px]">
                        {(field.options || []).map((option) => (
                            <label key={option} className="flex items-center gap-[12px] cursor-pointer">
                                <Checkbox
                                    checked={selectedOptions.includes(option)}
                                    onCheckedChange={(checked) => {
                                        if (checked) {
                                            onChange([...selectedOptions, option]);
                                        } else {
                                            onChange(selectedOptions.filter((o) => o !== option));
                                        }
                                    }}
                                />
                                <span className="text-[14px] text-foreground">{option}</span>
                            </label>
                        ))}
                    </div>
                </Field>
            );
        }

        case 'signature':
            return (
                <SignatureField
                    label={field.label}
                    required={field.required}
                    value={value || null}
                    onChange={(signatureData) => onChange(signatureData)}
                />
            );

        default:
            return (
                <div className="p-[16px] bg-warning/10 border border-warning/30 rounded-[12px]">
                    <p className="text-[14px] text-warning">{t('procedures:typeDeChampNonSupporte')} {field.type}</p>
                </div>
            );
    }
}

// ─── Composant principal ────────────────────────────────────────────────────────

/**
 * Formulaire partagé pour la création et l'édition de rapports de procédure.
 *
 * @param {object} props
 * @param {string} props.title - Titre affiché dans le header
 * @param {string} props.color - Couleur du template/rapport
 * @param {object} props.currentSection - Section courante { id, name/title, icon, description, fields }
 * @param {number} props.currentSectionIndex - Index de la section courante
 * @param {number} props.totalSections - Nombre total de sections
 * @param {object} props.formData - Données du formulaire (flat)
 * @param {object} props.uploadedPhotos - Photos par champ
 * @param {string|null} props.isUploadingPhoto - fieldId en cours d'upload
 * @param {boolean} props.showPhotoCapture - Afficher la sheet de capture photo
 * @param {Function} props.onFieldChange - Handler changement de champ
 * @param {Function} props.onPhotoCapture - Handler ouverture capture photo
 * @param {Function} props.onPhotoCaptured - Handler photo capturée
 * @param {Function} props.onRemovePhoto - Handler suppression photo (edit only)
 * @param {Function} props.onClosePhotoCapture - Handler fermeture capture photo
 * @param {Function} props.onNext - Handler bouton suivant
 * @param {Function} props.onPrev - Handler bouton précédent
 * @param {Function} props.onBack - Handler bouton retour (header)
 * @param {boolean} [props.canProceed=true] - Le bouton suivant est-il activable
 * @param {boolean} [props.isSubmitting=false] - Soumission en cours
 * @param {string} [props.lastButtonLabel] - Label du bouton sur la dernière section
 * @param {string} [props.stepLabel] - Label d'étape personnalisé (ex: "Étape 2 sur 5")
 * @param {string} [props.mode='create'] - 'create' ou 'edit'
 * @param {string} [props.sourceUrl] - URL source pour navigation photo (edit)
 * @param {React.ReactNode} [props.headerExtra] - Contenu supplémentaire dans le header (badge brouillon, etc.)
 * @param {React.ReactNode} [props.beforeContent] - Contenu avant les sections (étape dossier)
 * @param {React.ReactNode} [props.afterContent] - Contenu après le formulaire (sheets, dialogs)
 * @param {number} [props.progress] - Pourcentage de progression (override le calcul par défaut)
 * @param {boolean} [props.showFooter=true] - Afficher le footer de navigation
 */
export default function ProcedureReportForm({
    title,
    color: templateColor,
    currentSection,
    currentSectionIndex,
    totalSections,
    progress,
    formData,
    uploadedPhotos,
    isUploadingPhoto,
    showPhotoCapture,
    captureFieldId,
    onFieldChange,
    onPhotoCapture,
    onPhotoCaptured,
    onRemovePhoto,
    onClosePhotoCapture,
    onNext,
    onPrev,
    onBack,
    canProceed = true,
    isSubmitting = false,
    lastButtonLabel = 'Terminer et valider',
    stepLabel,
    mode = 'create',
    sourceUrl,
    headerExtra,
    beforeContent,
    afterContent,
    showFooter = true,
}) {
    const { t } = useTranslation();
    const isLastSection = currentSectionIndex === totalSections - 1;
    const scrollRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo(0, 0);
    }, [currentSectionIndex]);

    // Séparer champs photo / vidéo / réguliers
    const fields = currentSection?.fields || [];
    const photoFields = fields.filter((f) => backendToFrontendFieldType(f.type) === 'photo');
    const videoFields = fields.filter((f) => backendToFrontendFieldType(f.type) === 'video');
    const regularFields = fields.filter((f) => {
        const t = backendToFrontendFieldType(f.type);
        return t !== 'photo' && t !== 'video';
    });

    // Détermine si la sheet à ouvrir est celle de photo ou de vidéo
    const captureField = fields.find((f) => f.id === captureFieldId);
    const isVideoCapture = isVideoFieldType(captureField?.type);

    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-card shadow-sm">
                <div className="px-[20px] py-[16px] flex items-center gap-[12px]">
                    <BackButton onClick={onBack} />
                    <div className="flex-1">
                        <h1 className="text-[18px] text-foreground">{title}</h1>
                        <p className="text-[12px] text-muted-foreground">{stepLabel}</p>
                    </div>
                    {headerExtra}
                </div>
                {/* Progress Bar */}
                <div className="h-[4px] bg-surface">
                    <div
                        className="h-full transition-all duration-300"
                        style={{
                            width: `${progress != null ? progress : ((currentSectionIndex + 1) / totalSections) * 100}%`,
                            backgroundColor: templateColor,
                        }}
                    />
                </div>
            </div>

            {/* Content */}
            <div
                ref={scrollRef}
                className="flex-1 overflow-y-auto scrollbar-hide pb-[120px]"
                style={{
                    background: `linear-gradient(to right bottom, ${colorWithAlpha(templateColor, '15')}, ${colorWithAlpha(templateColor, '08')})`,
                }}
            >
                <div className="px-[20px] md:px-0 pt-[20px] md:max-w-[62.5%] md:min-w-[728px] mx-auto w-full">
                    {/* Contenu personnalisé avant la section (ex: StepSelectFolder) */}
                    {beforeContent}

                    {/* Section du formulaire */}
                    {currentSection && (
                        <div>
                            {/* Section Header */}
                            <div className="text-center mb-[24px]">
                                <div
                                    className="w-[48px] h-[48px] rounded-full flex items-center justify-center text-[24px] mx-auto mb-[12px] shadow-sm"
                                    style={{ backgroundColor: templateColor }}
                                >
                                    {currentSection.icon || '📋'}
                                </div>
                                <h2 className="text-[20px] text-foreground mb-[6px]">
                                    {currentSection.name || currentSection.title}
                                </h2>
                                {currentSection.description && (
                                    <p className="text-[13px] text-muted-foreground">{currentSection.description}</p>
                                )}
                            </div>

                            {/* Form Card for regular fields */}
                            {regularFields.length > 0 && (
                                <div className="bg-card rounded-[16px] p-[20px] mb-[20px] border border-border">
                                    <div className="space-y-[20px]">
                                        {regularFields.map((field) => (
                                            <FieldRenderer
                                                key={field.id}
                                                field={field}
                                                value={formData[field.id]}
                                                onChange={(value) => onFieldChange(field.id, value)}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Photo fields */}
                            {photoFields.map((field) => (
                                <div
                                    key={field.id}
                                    className="bg-card rounded-[16px] p-[20px] mb-[20px] border-2 border-secondary"
                                >
                                    {mode === 'edit' ? (
                                        <PhotoFieldEdit
                                            field={field}
                                            photos={uploadedPhotos[field.id] || []}
                                            onPhotoCapture={onPhotoCapture}
                                            onRemovePhoto={onRemovePhoto}
                                            isUploading={isUploadingPhoto === field.id}
                                            sourceUrl={sourceUrl}
                                        />
                                    ) : (
                                        <PhotoFieldCreate
                                            field={field}
                                            photos={uploadedPhotos[field.id] || []}
                                            onPhotoCapture={onPhotoCapture}
                                            isUploading={isUploadingPhoto === field.id}
                                        />
                                    )}
                                </div>
                            ))}

                            {/* Video fields */}
                            {videoFields.map((field) => (
                                <div
                                    key={field.id}
                                    className="bg-card rounded-[16px] p-[20px] mb-[20px] border-2 border-secondary"
                                >
                                    {mode === 'edit' ? (
                                        <VideoFieldEdit
                                            field={field}
                                            videos={uploadedPhotos[field.id] || []}
                                            onVideoCapture={onPhotoCapture}
                                            onRemoveVideo={onRemovePhoto}
                                            isUploading={isUploadingPhoto === field.id}
                                            sourceUrl={sourceUrl}
                                        />
                                    ) : (
                                        <VideoFieldCreate
                                            field={field}
                                            videos={uploadedPhotos[field.id] || []}
                                            onVideoCapture={onPhotoCapture}
                                            isUploading={isUploadingPhoto === field.id}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            {showFooter && (
                <div className="absolute bottom-0 left-0 right-0 bg-card border-t border-border px-[20px] py-[16px]">
                    <div className="flex gap-[12px] md:max-w-[62.5%] md:min-w-[728px] mx-auto">
                        {currentSectionIndex > 0 && (
                            <Button variant="soft" color="neutral" size="cta" className="flex-1" onClick={onPrev}>
                                {t('procedures:precedent')}
                            </Button>
                        )}
                        <Button
                            variant="solid"
                            color="secondary"
                            size="cta"
                            onClick={onNext}
                            disabled={!canProceed || isSubmitting || !!isUploadingPhoto}
                            className={`flex-1 ${
                                !canProceed || isSubmitting || !!isUploadingPhoto
                                    ? 'bg-surface text-muted-foreground cursor-not-allowed'
                                    : ''
                            }`}
                        >
                            {isSubmitting && <Loader2 className="h-5 w-5 animate-spin" />}
                            {isLastSection ? (
                                lastButtonLabel
                            ) : (
                                <>
                                    {t('procedures:suivant')}
                                    <ChevronRight className="h-5 w-5" />
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            )}

            {/* Sheet de capture : une dédiée photo, une dédiée vidéo, selon le champ actif */}
            {isVideoCapture ? (
                <VideoCaptureSheet
                    open={showPhotoCapture}
                    onClose={onClosePhotoCapture}
                    onCapture={onPhotoCaptured}
                    isUploading={!!isUploadingPhoto}
                />
            ) : (
                <PhotoCaptureSheet
                    open={showPhotoCapture}
                    onClose={onClosePhotoCapture}
                    onCapture={onPhotoCaptured}
                    isUploading={!!isUploadingPhoto}
                />
            )}

            {/* Contenu après (sheets de finalisation, dialogs, etc.) */}
            {afterContent}
        </div>
    );
}

// ─── Exports utilitaires ────────────────────────────────────────────────────────

/**
 * Skeleton de chargement
 */
export function LoadingSkeleton({ onBack }) {
    const { t } = useTranslation();
    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            <div className="bg-card shadow-sm px-[20px] py-[16px] flex items-center gap-[12px]">
                <BackButton onClick={onBack} />
                <h1 className="text-[18px] text-foreground">{t('procedures:chargement')}</h1>
            </div>
            <div className="flex-1 flex items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        </div>
    );
}

/**
 * État d'erreur
 */
export function ErrorState({ message, onRetry, onBack }) {
    const { t } = useTranslation();
    return (
        <div className="bg-surface relative size-full overflow-hidden flex flex-col">
            <div className="bg-card shadow-sm px-[20px] py-[16px] flex items-center gap-[12px]">
                <BackButton onClick={onBack} />
                <h1 className="text-[18px] text-foreground">{t('procedures:erreur')}</h1>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-6">
                <AlertCircle className="w-12 h-12 text-destructive/60 mb-4" />
                <p className="text-[14px] text-muted-foreground text-center mb-4">{message}</p>
                {onRetry && (
                    <Button variant="outline" color="neutral" onClick={onRetry}>
                        {t('procedures:reessayer')}
                    </Button>
                )}
            </div>
        </div>
    );
}
